import { Flight, Flights } from "@/lib/Types/Flight";

export interface FlightChangeResult {
  outwardFlightChanged: boolean;
  returnFlightChanged: boolean;
}

/**
 * Compares original flight data with confirmed flight data from QuickVacancy response.
 * Detects changes in departure/destination localities and datetime.
 *
 * Comparison points (matching old app logic):
 * 1. Departure locality (first flight segment)
 * 2. Destination locality (last flight segment)
 * 3. Departure datetime (first flight segment)
 * 4. Destination datetime (last flight segment)
 */
export function compareFlights(
  originalFlights: Flights | null | undefined,
  confirmedFlights: Flights | null | undefined
): FlightChangeResult {
  if (!originalFlights || !confirmedFlights) {
    return { outwardFlightChanged: false, returnFlightChanged: false };
  }

  const compareFlightSegments = (
    original: Flight[] | undefined,
    confirmed: Flight[] | undefined
  ): boolean => {
    if (!original?.length || !confirmed?.length) return false;

    const origFirst = original[0];
    const origLast = original[original.length - 1];
    const confFirst = confirmed[0];
    const confLast = confirmed[confirmed.length - 1];

    // Compare departure locality
    if (origFirst.departure.locality !== confFirst.departure.locality) {
      return true;
    }

    // Compare destination locality
    if (origLast.destination.locality !== confLast.destination.locality) {
      return true;
    }

    // Compare departure datetime
    if (
      new Date(origFirst.departure.datetime).getTime() !==
      new Date(confFirst.departure.datetime).getTime()
    ) {
      return true;
    }

    // Compare destination datetime
    if (
      new Date(origLast.destination.datetime).getTime() !==
      new Date(confLast.destination.datetime).getTime()
    ) {
      return true;
    }

    return false;
  };

  return {
    outwardFlightChanged: compareFlightSegments(
      originalFlights.outwardFlight,
      confirmedFlights.outwardFlight
    ),
    returnFlightChanged: compareFlightSegments(
      originalFlights.returnFlight,
      confirmedFlights.returnFlight
    ),
  };
}
